import { IDENTITY_MAT4, normalizedModelSchema } from "@voxelspy/contracts";
import { ANALYSIS_LIMITS } from "./analyze.js";
import { countExpandedGeometry } from "./geometry.js";
import { summarizeModelGeometryWithEvidence, } from "./summary.js";
/**
 * Failed-closed rejection of hostile or oversized input, distinct from a
 * contract-schema validation failure (which throws the Zod error from
 * `normalizedModelSchema.parse` directly, matching `analyzeModelPair`'s
 * existing convention). Thrown before any O(vertices + triangles) work runs.
 */
export class InspectionResourceLimitError extends Error {
    constructor(message) {
        super(message);
        this.name = "InspectionResourceLimitError";
    }
}
/** Default number of representative example locations collected per topology issue kind. */
export const DEFAULT_MAX_TOPOLOGY_EXAMPLES = 5;
/** Implementation ceiling on `InspectOptions.maxTopologyExamples`. */
export const MAX_TOPOLOGY_EXAMPLES = 50;
/** Default number of mesh-breakdown entries reported before truncation. */
export const DEFAULT_MAX_MESH_BREAKDOWN_ENTRIES = 200;
/** Implementation ceiling on `InspectOptions.maxMeshBreakdownEntries`. */
export const MAX_MESH_BREAKDOWN_ENTRIES = 2_000;
/**
 * Builds a single-model inspection result that a UI can render directly:
 * the existing geometry summary (reused, not recomputed) plus a bounded,
 * deterministic list of topology findings, a watertightness verdict, and a
 * per-mesh breakdown. This is a reporting layer over
 * `summarizeModelGeometry`'s measurements, not a second geometry pipeline --
 * it adds no new geometric math beyond bounded example-location collection
 * (see `summarizeModelGeometryWithEvidence` in `summary.ts`).
 *
 * Fails closed: `model` is validated against the contracts schema (throwing
 * the Zod error on failure, matching `analyzeModelPair`'s convention), and
 * expanded vertex/triangle counts are checked against this package's
 * existing `ANALYSIS_LIMITS` ceilings before any O(vertices + triangles)
 * work runs, throwing `InspectionResourceLimitError` if exceeded.
 */
export function inspectModel(model, options = {}) {
    const validated = normalizedModelSchema.parse(model);
    checkResourceCeiling(validated);
    const maxTopologyExamples = resolveBound(options.maxTopologyExamples, DEFAULT_MAX_TOPOLOGY_EXAMPLES, MAX_TOPOLOGY_EXAMPLES, "maxTopologyExamples");
    const maxMeshBreakdownEntries = resolveBound(options.maxMeshBreakdownEntries, DEFAULT_MAX_MESH_BREAKDOWN_ENTRIES, MAX_MESH_BREAKDOWN_ENTRIES, "maxMeshBreakdownEntries");
    const modelToComparison = options.modelToComparison ?? IDENTITY_MAT4;
    const { summary, evidence } = summarizeModelGeometryWithEvidence(validated, modelToComparison, maxTopologyExamples);
    return {
        modelId: validated.id,
        frame: validated.frame,
        provenance: validated.provenance,
        summary,
        topologyFindings: buildTopologyFindings(evidence),
        watertightness: computeWatertightness(summary),
        meshBreakdown: buildMeshBreakdown(validated, maxMeshBreakdownEntries),
    };
}
/**
 * Shared by `diagnoseMeshHealth` (`src/diagnose.ts`) so the two entry points
 * enforce identical expanded-geometry ceilings rather than drifting apart.
 */
export function checkResourceCeiling(model) {
    const { vertices, triangles } = countExpandedGeometry(model);
    if (vertices > ANALYSIS_LIMITS.maxExpandedVertices) {
        throw new InspectionResourceLimitError(`Expanded geometry requires ${vertices} vertices; the implementation ceiling is ${ANALYSIS_LIMITS.maxExpandedVertices}.`);
    }
    if (triangles > ANALYSIS_LIMITS.maxExpandedTriangles) {
        throw new InspectionResourceLimitError(`Expanded geometry requires ${triangles} triangles; the implementation ceiling is ${ANALYSIS_LIMITS.maxExpandedTriangles}.`);
    }
}
/** Shared by `diagnoseMeshHealth` (`src/diagnose.ts`) for identical option-bound validation. */
export function resolveBound(value, fallback, ceiling, name) {
    if (value === undefined)
        return fallback;
    if (!Number.isSafeInteger(value) || value < 0 || value > ceiling) {
        throw new RangeError(`${name} must be a safe integer between 0 and ${ceiling}; received ${String(value)}.`);
    }
    return value;
}
const TOPOLOGY_FINDING_ORDER = [
    // Boundary edges alone describe a valid, intentionally open surface (a
    // panel or sheet, for example) rather than damaged geometry, so this kind
    // is informational. The others describe conditions that make the surface
    // itself unreliable (a genuine manifold defect, an orientation that
    // breaks normal/volume sign, or a triangle contributing no real area).
    { kind: "boundary-edges", severity: "info" },
    { kind: "non-manifold-edges", severity: "warning" },
    { kind: "inconsistent-orientation", severity: "warning" },
    { kind: "degenerate-triangles", severity: "warning" },
];
function buildTopologyFindings(evidence) {
    const countAndExamplesFor = (kind) => {
        switch (kind) {
            case "boundary-edges":
                return {
                    count: evidence.boundaryEdgeCount,
                    examples: evidence.boundaryEdgeExamples,
                };
            case "non-manifold-edges":
                return {
                    count: evidence.nonManifoldEdgeCount,
                    examples: evidence.nonManifoldEdgeExamples,
                };
            case "inconsistent-orientation":
                return {
                    count: evidence.inconsistentEdgeCount,
                    examples: evidence.inconsistentEdgeExamples,
                };
            case "degenerate-triangles":
                return {
                    count: evidence.degenerateTriangleCount,
                    examples: evidence.degenerateTriangleExamples,
                };
        }
    };
    const findings = [];
    for (const { kind, severity } of TOPOLOGY_FINDING_ORDER) {
        const { count, examples } = countAndExamplesFor(kind);
        if (count === 0)
            continue;
        findings.push({
            id: kind,
            kind,
            severity,
            count,
            summary: describeFinding(kind, count),
            examples,
            examplesTruncated: examples.length < count,
        });
    }
    return findings;
}
function describeFinding(kind, count) {
    const plural = count === 1 ? "" : "s";
    switch (kind) {
        case "boundary-edges":
            return `${count} boundary edge${plural}: the surface is open along ${count === 1 ? "this edge" : "these edges"} (touched by exactly one triangle).`;
        case "non-manifold-edges":
            return `${count} non-manifold edge${plural}: shared by more than two triangle corners at the same exact coordinates.`;
        case "inconsistent-orientation":
            return `${count} inconsistently oriented edge${plural}: traversed twice in the same winding direction instead of once each way.`;
        case "degenerate-triangles":
            return `${count} degenerate triangle${plural}: zero or non-finite area.`;
    }
}
function computeWatertightness(summary) {
    if (summary.triangleCount === 0) {
        return { state: "indeterminate", reasons: ["empty-geometry"] };
    }
    const topology = summary.volume.topology;
    const reasons = [];
    if (topology.boundaryEdgeCount > 0)
        reasons.push("boundary-edges");
    if (topology.nonManifoldEdgeCount > 0)
        reasons.push("non-manifold-edges");
    if (reasons.length === 0)
        return { state: "closed" };
    return { state: "not-closed", reasons };
}
function buildMeshBreakdown(model, maxEntries) {
    const totalMeshCount = model.meshes.length;
    const meshes = model.meshes.slice(0, maxEntries).map((mesh) => ({
        meshId: mesh.id,
        triangleCount: mesh.geometry.indices.length / 3,
        vertexCount: mesh.geometry.positions.length / 3,
    }));
    return {
        meshes,
        truncated: totalMeshCount > maxEntries,
        totalMeshCount,
    };
}
